import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Megaphone, Plus, Eye, MousePointerClick, Play, Pause, 
  Edit2, Trash2, TrendingUp, Sparkles, ArrowLeft 
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminSystemPromotionsPage = () => {
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [filterStatus, setFilterStatus] = useState('active');

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [promotionsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/ads/admin/promotions${filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`, {
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/ads/admin/promotions/stats/summary`, {
          credentials: 'include'
        })
      ]);

      if (promotionsRes.ok) {
        const data = await promotionsRes.json();
        setPromotions(data.promotions || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats);
      }
    } catch (error) {
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-gray-100 text-gray-800';
  };

  const getPromotionTypeColor = (type) => {
    const colors = {
      feature: 'bg-blue-100 text-blue-800',
      announcement: 'bg-yellow-100 text-yellow-800',
      education: 'bg-purple-100 text-purple-800',
      partner: 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/admin/marketing/ads')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Megaphone className="h-8 w-8 text-purple-600" />
                System Promotions
              </h1>
              <p className="text-muted-foreground">Manage platform-wide promotional campaigns</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Promotion
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-green-600">{stats.active_promotions}</div>
                <p className="text-sm text-muted-foreground">Active Promotions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{stats.total_promotions}</div>
                <p className="text-sm text-muted-foreground">Total Promotions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-blue-600">
                  {stats.total_impressions.toLocaleString()}
                </div>
                <p className="text-sm text-muted-foreground">Total Impressions</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold text-purple-600">{stats.overall_ctr.toFixed(2)}%</div>
                <p className="text-sm text-muted-foreground">Overall CTR</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2">
              {['active', 'paused', 'all'].map((status) => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Promotions List */}
        <div className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">Loading...</CardContent>
            </Card>
          ) : promotions.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <Megaphone className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No promotions found</p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Promotion
                </Button>
              </CardContent>
            </Card>
          ) : (
            promotions.map((promo) => (
              <PromotionCard
                key={promo.id}
                promotion={promo}
                onRefresh={fetchData}
                onEdit={setEditingPromotion}
                getStatusColor={getStatusColor}
                getPromotionTypeColor={getPromotionTypeColor}
              />
            ))
          )}
        </div>

        {/* Create/Edit Modal */}
        {(showCreateModal || editingPromotion) && (
          <PromotionFormModal
            promotion={editingPromotion}
            onClose={() => {
              setShowCreateModal(false);
              setEditingPromotion(null);
            }}
            onSuccess={() => {
              setShowCreateModal(false);
              setEditingPromotion(null);
              fetchData();
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Promotion Card Component
const PromotionCard = ({ promotion, onRefresh, onEdit, getStatusColor, getPromotionTypeColor }) => {
  const [processing, setProcessing] = useState(false);

  const handleToggleStatus = async () => {
    setProcessing(true);
    try {
      const endpoint = promotion.status === 'active' 
        ? `${API_URL}/api/ads/admin/promotions/${promotion.id}/pause`
        : `${API_URL}/api/ads/admin/promotions/${promotion.id}/activate`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(promotion.status === 'active' ? 'Promotion paused' : 'Promotion activated');
        onRefresh();
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this promotion?')) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/promotions/${promotion.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Promotion deleted');
        onRefresh();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Image */}
          {promotion.image_url && (
            <div className="w-full md:w-48 h-32 flex-shrink-0">
              <img
                src={promotion.image_url}
                alt={promotion.headline}
                className="w-full h-full object-cover rounded"
              />
            </div>
          )}

          {/* Details */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-xl font-bold">{promotion.promotion_name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getPromotionTypeColor(promotion.promotion_type)}>
                    {promotion.promotion_type}
                  </Badge>
                  <Badge className={getStatusColor(promotion.status)}>
                    {promotion.status}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="font-semibold mb-1">{promotion.headline}</p>
              <p className="text-sm text-muted-foreground mb-2">{promotion.description}</p>
              <a
                href={promotion.destination_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                {promotion.destination_link}
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Placement</div>
                <div className="font-semibold capitalize">{promotion.placement}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Priority</div>
                <div className="font-semibold">{promotion.priority}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Impressions</div>
                <div className="font-semibold">{promotion.impressions || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Clicks</div>
                <div className="font-semibold">{promotion.clicks || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">CTR</div>
                <div className="font-semibold">{promotion.ctr?.toFixed(2) || 0}%</div>
              </div>
            </div>

            {/* Dates */}
            {(promotion.start_date || promotion.end_date) && (
              <div className="text-sm text-muted-foreground mb-4">
                {promotion.start_date && `From: ${new Date(promotion.start_date).toLocaleDateString()}`}
                {promotion.end_date && ` • Until: ${new Date(promotion.end_date).toLocaleDateString()}`}
                {!promotion.end_date && promotion.start_date && ' • Always Active'}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                onClick={handleToggleStatus}
                disabled={processing}
              >
                {promotion.status === 'active' ? (
                  <><Pause className="h-4 w-4 mr-1" /> Pause</>
                ) : (
                  <><Play className="h-4 w-4 mr-1" /> Activate</>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEdit(promotion)}
                disabled={processing}
              >
                <Edit2 className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handleDelete}
                disabled={processing}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Promotion Form Modal Component
const PromotionFormModal = ({ promotion, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    promotion_name: promotion?.promotion_name || '',
    promotion_type: promotion?.promotion_type || 'feature',
    headline: promotion?.headline || '',
    description: promotion?.description || '',
    cta_text: promotion?.cta_text || 'Learn More',
    destination_link: promotion?.destination_link || '',
    placement: promotion?.placement || 'all',
    image_url: promotion?.image_url || '',
    start_date: promotion?.start_date ? promotion.start_date.split('T')[0] : '',
    end_date: promotion?.end_date ? promotion.end_date.split('T')[0] : '',
    priority: promotion?.priority || 1
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = promotion
        ? `${API_URL}/api/ads/admin/promotions/${promotion.id}`
        : `${API_URL}/api/ads/admin/promotions/create`;
      
      const method = promotion ? 'PUT' : 'POST';

      // Format dates
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Failed to save promotion');

      toast.success(promotion ? 'Promotion updated!' : 'Promotion created and activated!');
      onSuccess();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="max-w-2xl w-full my-8">
        <CardHeader>
          <CardTitle>{promotion ? 'Edit' : 'Create'} System Promotion</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Promotion Name *</Label>
              <Input
                value={formData.promotion_name}
                onChange={(e) => setFormData({ ...formData, promotion_name: e.target.value })}
                placeholder="e.g., Summer Feature Launch"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Promotion Type *</Label>
                <select
                  value={formData.promotion_type}
                  onChange={(e) => setFormData({ ...formData, promotion_type: e.target.value })}
                  className="w-full border rounded-md p-2"
                  required
                >
                  <option value="feature">Platform Feature</option>
                  <option value="announcement">Announcement</option>
                  <option value="education">Educational</option>
                  <option value="partner">Partner/Sponsor</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label>Placement *</Label>
                <select
                  value={formData.placement}
                  onChange={(e) => setFormData({ ...formData, placement: e.target.value })}
                  className="w-full border rounded-md p-2"
                  required
                >
                  <option value="all">All Placements</option>
                  <option value="offers">Offers</option>
                  <option value="marketplace">Marketplace</option>
                  <option value="investments">Investments</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Headline *</Label>
              <Input
                value={formData.headline}
                onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                placeholder="Catchy headline"
                maxLength={100}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
                rows={3}
                maxLength={250}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Call To Action *</Label>
                <Input
                  value={formData.cta_text}
                  onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                  placeholder="e.g., Learn More"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Destination Link *</Label>
                <Input
                  value={formData.destination_link}
                  onChange={(e) => setFormData({ ...formData, destination_link: e.target.value })}
                  placeholder="https://..."
                  type="url"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Leave empty for immediate activation</p>
              </div>

              <div className="space-y-2">
                <Label>End Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">Leave empty for always active</p>
              </div>

              <div className="space-y-2">
                <Label>Priority (1-5)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : promotion ? 'Update Promotion' : 'Create & Activate'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
